import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** Minimum horizontal travel (px) before a left swipe closes the drawer. */
  #swipeThreshold = 50;

  /** @type {number} */
  #touchStartX = 0;

  /** @type {number} */
  #touchStartY = 0;

  /** Whether a single-finger gesture is currently being tracked for swipe-to-close. */
  /** @type {boolean} */
  #swipeTracking = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Swipe-left-to-close on the drawer panel (touch devices only). Passive
    // listeners keep vertical scrolling smooth; we only act on touchend.
    const { menuDrawer } = this.refs;
    menuDrawer.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    menuDrawer.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    menuDrawer.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    menuDrawer.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    const { menuDrawer } = this.refs;
    menuDrawer.removeEventListener('touchstart', this.#onTouchStart);
    menuDrawer.removeEventListener('touchmove', this.#onTouchMove);
    menuDrawer.removeEventListener('touchend', this.#onTouchEnd);
    menuDrawer.removeEventListener('touchcancel', this.#onTouchCancel);
  }

  /**
   * Records the starting point of a single-finger touch so touchend can decide
   * whether it was a left swipe.
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    if (!this.isOpen || event.touches.length !== 1) {
      this.#swipeTracking = false;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#swipeTracking = true;
  };

  /**
   * Abandons swipe tracking if the gesture becomes multi-touch (e.g. pinch).
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    if (this.#swipeTracking && event.touches.length !== 1) {
      this.#swipeTracking = false;
    }
  };

  /**
   * Closes the drawer when the gesture is a deliberate left swipe: horizontal
   * travel past the threshold that dominates any vertical movement (so it never
   * fires during vertical scrolling).
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    if (!this.#swipeTracking) return;
    this.#swipeTracking = false;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;

    if (deltaX < -this.#swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.close();
    }
  };

  /** @param {TouchEvent} _event */
  #onTouchCancel = (_event) => {
    this.#swipeTracking = false;
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
